import * as React from "react";
import { AlertTriangle, Inbox, RotateCcw } from "lucide-react";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

/** Skeleton baris tabel — taruh langsung di dalam `<tbody>` saat `loading`,
 *  jumlah kolom disesuaikan dengan header tabel aslinya supaya lebar kolom
 *  tidak "loncat" begitu data asli masuk.
 *
 *  Contoh:
 *    <tbody>
 *      {loading ? <SkeletonBaris baris={5} kolom={6} /> : data.map(row => ...)}
 *    </tbody>
 */
export function SkeletonBaris({ baris = 5, kolom = 4, className }) {
  return (
    <>
      {Array.from({ length: baris }).map((_, i) => (
        <tr key={i} className={cn("border-b border-slate-100 last:border-0", className)}>
          {Array.from({ length: kolom }).map((__, j) => (
            <td key={j} className="py-3 px-4">
              <Skeleton className="h-4 w-full max-w-[160px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** Skeleton kartu — grid kartu kosong menyerupai kpi_card/dashboard card
 *  (`bg-white border border-slate-200 rounded-xl shadow-sm`). Bisa dipakai
 *  berdiri sendiri di dalam grid manapun, tidak harus lewat `<LoadingState>`. */
export function SkeletonKartu({ jumlah = 3, className }) {
  return (
    <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4", className)}>
      {Array.from({ length: jumlah }).map((_, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-3">
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-3 w-full" />
        </div>
      ))}
    </div>
  );
}

/** Terjemahkan error axios jadi kalimat Indonesia yang sopan — pola yang sama
 *  dengan jaring pengaman `unhandledrejection` di `src/index.js`, dipakai lagi
 *  di sini supaya pesan di halaman konsisten dengan toast global. */
function pesanErrorDefault(error) {
  const status = error?.response?.status;
  return (
    error?.response?.data?.detail
    || (status ? `Gagal memuat data (kode ${status}).` : "Tidak bisa terhubung ke server. Periksa koneksi Anda.")
  );
}

/**
 * Pembungkus keadaan muat data yang seragam untuk satu blok konten (tabel,
 * grid kartu, atau apa pun): skeleton saat `loading`, pesan gagal + tombol
 * "Coba lagi" saat `error`, pesan kosong saat data sukses dimuat tapi nihil,
 * atau `children` saat semuanya beres.
 *
 * TIDAK menambah timeout baru — `error` yang masuk ke sini diasumsikan sudah
 * lewat timeout 30 detik + retry 1x bawaan `lib/api.js`. Komponen ini cuma
 * menentukan TAMPILANNYA di halaman, bukan mekanisme network-nya.
 *
 * Props:
 *  - loading      : boolean
 *  - error        : objek error axios, atau string pesan siap-tampil, atau null/undefined
 *  - kosong       : boolean — true kalau fetch sukses tapi datanya nihil
 *  - onRetry      : () => void, dipanggil saat tombol "Coba lagi" diklik.
 *                   Kalau tidak diberikan, tombol tidak ditampilkan.
 *  - varian       : "tabel" | "kartu" (default "kartu") — bentuk skeleton
 *                   default saat loading
 *  - jumlahBaris  : dipakai kalau varian="tabel" (default 5)
 *  - kolom        : dipakai kalau varian="tabel" (default 4)
 *  - jumlahKartu  : dipakai kalau varian="kartu" (default 3)
 *  - pesanKosong  : teks saat `kosong` (default "Belum ada data.")
 *  - renderSkeleton: () => ReactNode — override total tampilan skeleton
 *                   (mis. bentuk skeleton khusus), mengabaikan `varian`
 *  - data-testid  : id dasar; turunannya `${id}-coba-lagi` pada tombol retry
 *
 * Contoh (tabel):
 *   <table>
 *     <thead>...</thead>
 *     <tbody>
 *       <LoadingState loading={loading} error={error} kosong={!loading && !error && rows.length === 0}
 *         onRetry={fetchData} varian="tabel" kolom={6} data-testid="loading-produk">
 *         {rows.map(row => <tr key={row.id}>...</tr>)}
 *       </LoadingState>
 *     </tbody>
 *   </table>
 *
 * CATATAN: kalau dipakai di dalam `<tbody>` seperti contoh di atas, keadaan
 * error/kosong akan dirender sebagai satu <tr><td colSpan></td></tr> — atur
 * `kolom` sama dengan jumlah kolom tabel supaya pesan melebar penuh.
 */
export default function LoadingState({
  loading,
  error,
  kosong = false,
  onRetry,
  varian = "kartu",
  jumlahBaris = 5,
  kolom = 4,
  jumlahKartu = 3,
  pesanKosong = "Belum ada data.",
  renderSkeleton,
  children,
  "data-testid": testId,
}) {
  const bungkusBaris = (isi) => (
    <tr data-testid={testId}>
      <td colSpan={kolom} className="py-10 px-4">
        {isi}
      </td>
    </tr>
  );

  if (loading) {
    if (renderSkeleton) return <>{renderSkeleton()}</>;
    if (varian === "tabel") return <SkeletonBaris baris={jumlahBaris} kolom={kolom} />;
    return <SkeletonKartu jumlah={jumlahKartu} />;
  }

  if (error) {
    const isi = (
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <div className="w-11 h-11 rounded-full bg-rose-50 text-rose-600 grid place-items-center">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <p className="text-sm text-slate-600 max-w-sm">
          {typeof error === "string" ? error : pesanErrorDefault(error)}
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            data-testid={testId ? `${testId}-coba-lagi` : "btn-coba-lagi"}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-sm font-medium rounded-lg shadow-sm transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Coba lagi
          </button>
        )}
      </div>
    );
    return varian === "tabel"
      ? bungkusBaris(isi)
      : <div data-testid={testId} className="flex items-center justify-center py-12">{isi}</div>;
  }

  if (kosong) {
    const isi = (
      <div className="flex flex-col items-center justify-center gap-2 text-center">
        <div className="w-11 h-11 rounded-full bg-slate-100 text-slate-400 grid place-items-center">
          <Inbox className="w-5 h-5" />
        </div>
        <p className="text-sm text-slate-500">{pesanKosong}</p>
      </div>
    );
    return varian === "tabel"
      ? bungkusBaris(isi)
      : <div data-testid={testId} className="flex items-center justify-center py-12">{isi}</div>;
  }

  return <>{children}</>;
}
